import React, { useState } from 'react';
import { Trash2, Plus, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputClass = "w-full bg-black border border-white/10 rounded-none px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-white/30 transition-colors";
const btnClass = "bg-white text-black hover:bg-gray-200 rounded-none text-sm uppercase tracking-widest transition-all duration-300";

const FIELD_TYPES = [
  { value: 'text', label: 'Text Input' },
  { value: 'textarea', label: 'Text Area' },
  { value: 'dropdown', label: 'Dropdown' },
];

export const FormFieldsTab = ({ formFields, onRefresh }) => {
  const [newField, setNewField] = useState({ label: '', field_type: 'text', options: [], required: false });
  const [newOption, setNewOption] = useState('');

  const handleAddOption = () => {
    if (!newOption.trim()) return;
    setNewField({ ...newField, options: [...newField.options, newOption.trim()] });
    setNewOption('');
  };

  const handleRemoveOption = (index) => {
    setNewField({ ...newField, options: newField.options.filter((_, i) => i !== index) });
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newField.label.trim()) return;
    try {
      await axios.post(`${API}/form-fields`, newField);
      toast.success('Form field added');
      setNewField({ label: '', field_type: 'text', options: [], required: false });
      onRefresh();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to add field'); }
  };

  const handleDelete = async (fieldId) => {
    if (!window.confirm('Delete this form field?')) return;
    try {
      await axios.delete(`${API}/form-fields/${fieldId}`);
      toast.success('Field deleted');
      onRefresh();
    } catch { toast.error('Failed to delete field'); }
  };

  return (
    <div>
      <h2 className="text-2xl font-normal mb-6" style={{ fontFamily: 'Playfair Display, serif' }}>Enquiry Form Fields</h2>
      <p className="text-sm text-gray-400 mb-8">Add custom fields to the enquiry form. Name, Email, Shoot Type, and Message are always included.</p>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
        <div className="border border-white/10 p-8">
          <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Add Custom Field</h3>
          <form onSubmit={handleAdd} className="space-y-4" data-testid="add-form-field">
            <div>
              <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Field Label</label>
              <input type="text" value={newField.label} onChange={(e) => setNewField({ ...newField, label: e.target.value })} placeholder="e.g. Budget Range, Preferred Date" className={inputClass} required data-testid="field-label-input" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Field Type</label>
              <select value={newField.field_type} onChange={(e) => setNewField({ ...newField, field_type: e.target.value, options: [] })} className={`${inputClass} appearance-none`} data-testid="field-type-select">
                {FIELD_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>

            {newField.field_type === 'dropdown' && (
              <div>
                <label className="block text-xs uppercase tracking-widest text-gray-500 mb-1">Dropdown Options</label>
                <div className="flex gap-2 mb-2">
                  <input type="text" value={newOption} onChange={(e) => setNewOption(e.target.value)} placeholder="Add an option" className={`flex-1 ${inputClass}`} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddOption(); } }} data-testid="field-option-input" />
                  <button type="button" onClick={handleAddOption} className={`${btnClass} px-4 py-2`} data-testid="add-option-btn"><Plus size={16} /></button>
                </div>
                {newField.options.length > 0 && (
                  <div className="space-y-1">
                    {newField.options.map((opt, i) => (
                      <div key={i} className="flex items-center justify-between border border-white/10 px-3 py-2 text-sm">
                        <span>{opt}</span>
                        <button type="button" onClick={() => handleRemoveOption(i)} className="text-gray-500 hover:text-red-400"><X size={14} /></button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={newField.required} onChange={(e) => setNewField({ ...newField, required: e.target.checked })} className="w-4 h-4 accent-white" data-testid="field-required-check" />
              <span className="text-sm text-gray-400">Required field</span>
            </label>

            <button type="submit" className={`w-full ${btnClass} px-8 py-4`} data-testid="add-field-btn">Add Field</button>
          </form>
        </div>

        <div className="border border-white/10 p-8">
          <h3 className="text-lg font-normal mb-4" style={{ fontFamily: 'Playfair Display, serif' }}>Current Custom Fields ({formFields.length})</h3>
          {formFields.length === 0 ? (
            <p className="text-gray-400 text-sm">No custom fields yet. The form includes Name, Email, Shoot Type, and Message by default.</p>
          ) : (
            <div className="space-y-3">
              {formFields.map((field) => (
                <div key={field.id} className="flex items-start justify-between gap-3 border border-white/10 p-4" data-testid={`form-field-${field.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium">{field.label}</p>
                    <p className="text-xs text-gray-500 mt-1">
                      {field.field_type === 'dropdown' ? `Dropdown: ${field.options?.join(', ')}` : field.field_type === 'textarea' ? 'Text Area' : 'Text Input'}
                      {field.required && ' · Required'}
                    </p>
                  </div>
                  <button onClick={() => handleDelete(field.id)} className="text-gray-500 hover:text-red-400 transition-colors shrink-0 p-1" data-testid={`delete-field-${field.id}`}><Trash2 size={16} /></button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default FormFieldsTab;
